import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import api from '../api/api';
import EducatorLayout from './components/EducatorLayout';
import { AuthContext } from '../auth/AuthContext';
import { listSubjects } from '../api/subjects';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');
const uniqueItems = (arr) => Array.from(new Set(arr));
const splitListInput = (value) => {
  if (Array.isArray(value)) return uniqueItems(value.map(cleanText).filter(Boolean));
  if (typeof value !== 'string') return [];
  return uniqueItems(
    value
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter(Boolean)
  );
};
const listToInput = (list) => {
  if (Array.isArray(list)) return list.join(', ');
  if (typeof list === 'string') return list.trim();
  return '';
};

const emptyEducatorProfile = {
  name: '',
  email: '',
  phone: '',
  country: '',
  province: '',
  town: '',
  suburb: '',
  curricula: '',
  gradeLevels: '',
  subjectFocus: '',
  subjects: [],
  tutoringOptions: [],
  availabilityTimezone: 'Africa/Johannesburg',
  availabilitySlots: [],
};

const TEXT_COLOR = 'rgb(0, 21, 74)';
const CARD_RADIUS = 3;
const TUTORING_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-person' },
];

const EducatorSettings = () => {
  const { token, user } = useContext(AuthContext);
  const [profile, setProfile] = useState(emptyEducatorProfile);
  const [profilePicture, setProfilePicture] = useState(null);
  const [preview, setPreview] = useState(null);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [subjects, setSubjects] = useState([]);

  const fileReaderRef = useRef(null);
  const dayOptions = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' },
  ];

  const fetchProfile = async () => {
    if (!token || !user?._id) return;
    setLoadingProfile(true);
    try {
      const { data } = await api.get(`/users/${user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile({
        name: data?.name || '',
        email: data?.email || '',
        phone: data?.phone || '',
        country: data?.country || '',
        province: data?.province || '',
        town: data?.town || '',
        suburb: data?.suburb || '',
        curricula: listToInput(data?.curricula),
        gradeLevels: listToInput(data?.gradeLevels),
        subjectFocus: listToInput(data?.subjectFocus),
        subjects: Array.isArray(data?.subjects) ? data.subjects : [],
        tutoringOptions: Array.isArray(data?.tutoringOptions) ? data.tutoringOptions : [],
        availabilityTimezone: data?.educatorAvailability?.timezone || 'Africa/Johannesburg',
        availabilitySlots: Array.isArray(data?.educatorAvailability?.slots) ? data.educatorAvailability.slots : [],
      });
      setPreview(data?.profilePictureUrl || null);
    } catch (err) {
      console.error('[Settings] fetchProfile error:', err);
      toast.error(err?.response?.data?.message || 'Failed to load profile.');
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const data = await listSubjects();
 setSubjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[Settings] fetchSubjects error:', err);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchSubjects();
    // eslint-disable-next-line
  }, [token, user?._id]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const toggleTutoringOption = (option) => {
    setProfile((prev) => {
 const current = Array.isArray(prev.tutoringOptions) ? prev.tutoringOptions : [];
      const next = new Set(current);
 next.has(option) ? next.delete(option) : next.add(option);
      return { ...prev, tutoringOptions: Array.from(next) };
    });
  };

  const updateAvailabilitySlot = (index, changes) => {
    setProfile((prev) => {
      const next = [...(prev.availabilitySlots || [])];
      next[index] = { ...next[index], ...changes };
      return { ...prev, availabilitySlots: next };
    });
  };

  const addAvailabilitySlot = () => {
    setProfile((prev) => ({
      ...prev,
      availabilitySlots: [
        ...(prev.availabilitySlots || []),
        { dayOfWeek: 1, startTime: '14:00', endTime: '16:00', mode: 'online' },
      ],
    }));
  };

  const removeAvailabilitySlot = (index) => {
    setProfile((prev) => ({
      ...prev,
      availabilitySlots: (prev.availabilitySlots || []).filter((_, idx) => idx !== index),
    }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files?.[0];
    setProfilePicture(file || null);

    if (file) {
      const reader = new FileReader();
      fileReaderRef.current = reader;
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  useEffect(() => {
    return () => {
      try {
        fileReaderRef.current?.abort?.();
      } catch (error) {
        console.debug('[Settings] file reader abort error', error);
      }
    };
  }, []);

  const validateProfile = () => {
    if (!profile.name.trim()) return toast.error('Name is required.'), false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) return toast.error('Valid email is required.'), false;
    if (profile.phone && !/^\+?\d{7,15}$/.test(profile.phone))
      return toast.error('Valid phone number is required.'), false;
    return true;
  };

  const updateProfile = async (event) => {
    event.preventDefault();
    if (!validateProfile()) return;

    setLoadingProfile(true);
    try {
      const payload = {
        name: cleanText(profile.name),
        email: cleanText(profile.email),
        phone: cleanText(profile.phone),
        country: cleanText(profile.country),
        province: cleanText(profile.province),
        town: cleanText(profile.town),
        suburb: cleanText(profile.suburb),
        curricula: splitListInput(profile.curricula),
        gradeLevels: splitListInput(profile.gradeLevels),
        subjectFocus: splitListInput(profile.subjectFocus),
 subjects: Array.isArray(profile.subjects) ? profile.subjects : [],
 tutoringOptions: Array.isArray(profile.tutoringOptions) ? profile.tutoringOptions : [],
        educatorAvailability: {
          timezone: profile.availabilityTimezone || 'Africa/Johannesburg',
          slots: (profile.availabilitySlots || [])
            .filter((slot) => slot && slot.startTime && slot.endTime)
            .map((slot) => ({
              dayOfWeek: Number(slot.dayOfWeek),
              startTime: slot.startTime,
              endTime: slot.endTime,
              mode: slot.mode || 'both',
            })),
        },
      };

      if (profilePicture) {
        const fd = new FormData();
        fd.append('name', payload.name);
        fd.append('email', payload.email);
        fd.append('phone', payload.phone);
        fd.append('country', payload.country);
        fd.append('province', payload.province);
        fd.append('town', payload.town);
        fd.append('suburb', payload.suburb);
        fd.append('curricula', JSON.stringify(payload.curricula));
        fd.append('gradeLevels', JSON.stringify(payload.gradeLevels));
        fd.append('subjectFocus', JSON.stringify(payload.subjectFocus));
        fd.append('subjects', JSON.stringify(payload.subjects));
        fd.append('tutoringOptions', JSON.stringify(payload.tutoringOptions));
        fd.append('educatorAvailability', JSON.stringify(payload.educatorAvailability));
        fd.append('profilePicture', profilePicture);
        await api.put(`/users/${user._id}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.put(`/users/${user._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      toast.success('Profile updated successfully');
      await fetchProfile();
    } catch (err) {
      console.error('[Settings] updateProfile error:', err);
      toast.error(err?.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const removeProfilePicture = async () => {
    if (!token || !user?._id) return;
    setLoadingProfile(true);
    try {
      await api.delete(`/users/${user._id}/profile-picture`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfilePicture(null);
      setPreview(null);
      toast.success('Profile picture removed');
    } catch (err) {
      console.error('[Settings] removeProfilePicture error:', err);
      toast.error(err?.response?.data?.message || 'Failed to remove picture');
    } finally {
      setLoadingProfile(false);
    }
  };

  const updatePassword = async (event) => {
    event.preventDefault();

    if (!passwords.current || !passwords.new || !passwords.confirm) {
      return toast.error('Please fill all password fields');
    }
    if (passwords.new !== passwords.confirm) {
      return toast.error('New passwords do not match');
    }
    if (passwords.new.length < 8) {
      return toast.error('New password must be at least 8 characters');
    }

    setLoadingPassword(true);
    try {
      await api.put(
        `/users/${user._id}/password`,
        { currentPassword: passwords.current, newPassword: passwords.new },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password updated successfully');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          const { data } = await api.post('/auth/refresh');
          const refreshedToken = data?.token;
          if (refreshedToken) {
            await api.put(
              `/users/${user._id}/password`,
              { currentPassword: passwords.current, newPassword: passwords.new },
              { headers: { Authorization: `Bearer ${refreshedToken}` } }
            );
            toast.success('Password updated successfully');
            setPasswords({ current: '', new: '', confirm: '' });
          }
        } catch (err2) {
          console.error('[Settings] password refresh error:', err2);
          toast.error(err2?.response?.data?.message || 'Failed to update password');
        }
      } else {
        toast.error(err?.response?.data?.message || 'Failed to update password');
      }
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <EducatorLayout>
      <Box sx={{ color: TEXT_COLOR }}>
        <Stack spacing={3}>
          <Card elevation={4} sx={{ borderRadius: CARD_RADIUS }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonRoundedIcon />
                </Avatar>
              }
              title={
                <Typography variant="h5" fontWeight={800} sx={{ color: TEXT_COLOR }}>
                  Profile settings
                </Typography>
              }
              subheader="Keep your profile information up to date so students know what you offer."
              subheaderTypographyProps={{ sx: { color: TEXT_COLOR } }}
            />
            <Divider />
            <CardContent>
              <Box component="form" onSubmit={updateProfile} noValidate>
                <Stack spacing={3}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Avatar
                      src={preview || undefined}
                      alt={profile.name || 'Profile preview'}
                      sx={{ width: 96, height: 96 }}
                    >
                      {profile.name?.charAt(0)?.toUpperCase() || 'T'}
                    </Avatar>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<FileUploadRoundedIcon />}
                        disabled={loadingProfile}
                      >
                        Upload photo
                        <input hidden accept="image/*" type="file" onChange={handleProfilePictureChange} />
                      </Button>
                      {preview && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          onClick={removeProfilePicture}
                          disabled={loadingProfile}
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Full name"
                        name="name"
                        value={profile.name}
                        onChange={handleProfileChange}
                        fullWidth
                        required
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        value={profile.email}
                        onChange={handleProfileChange}
                        fullWidth
                        required
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Phone number"
                        name="phone"
                        value={profile.phone}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Country"
                        name="country"
                        value={profile.country}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Province / State"
                        name="province"
                        value={profile.province}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Town or city"
                        name="town"
                        value={profile.town}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Suburb"
                        name="suburb"
                        value={profile.suburb}
                        onChange={handleProfileChange}
                        fullWidth
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Curricula (comma separated)"
                        name="curricula"
                        value={profile.curricula}
                        onChange={handleProfileChange}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Grades (comma separated)"
                        name="gradeLevels"
                        value={profile.gradeLevels}
                        onChange={handleProfileChange}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Subjects (comma separated)"
                        name="subjectFocus"
                        value={profile.subjectFocus}
                        onChange={handleProfileChange}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={loadingProfile}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        label="Subject allocations"
                        value={profile.subjects}
                        onChange={(event) =>
                          setProfile((prev) => ({ ...prev, subjects: event.target.value }))
                        }
                        fullWidth
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) =>
 selected && selected.length ? selected.join(', ') : 'Select subjects',
                        }}
                        disabled={loadingProfile}
                      >
                        {subjects.map((subject) => (
                          <MenuItem key={subject._id || subject.name} value={subject.name}>
                            {subject.name}
 {subject.grade != null ? ` (G${subject.grade})` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ color: TEXT_COLOR, fontWeight: 600, mb: 1 }}>
                        Tutoring options
                      </Typography>
                      <FormGroup row>
                        {TUTORING_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            control={
                              <Checkbox
                                checked={
                                  Array.isArray(profile.tutoringOptions) &&
                                  profile.tutoringOptions.includes(option.value)
                                }
                                onChange={() => toggleTutoringOption(option.value)}
                                disabled={loadingProfile}
                              />
                            }
                            label={option.label}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2" sx={{ color: TEXT_COLOR, fontWeight: 600 }}>
                          Availability schedule
                        </Typography>
                        {(profile.availabilitySlots || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No availability set yet.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {(profile.availabilitySlots || []).map((slot, index) => (
                              <Stack
                                key={`${slot.dayOfWeek}-${index}`}
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'stretch', md: 'center' }}
                              >
                                <TextField
                                  select
                                  label="Day"
                                  value={slot.dayOfWeek}
                                  onChange={(event) =>
                                    updateAvailabilitySlot(index, { dayOfWeek: Number(event.target.value) })
                                  }
                                  sx={{ minWidth: 160 }}
                                  disabled={loadingProfile}
                                >
                                  {dayOptions.map((day) => (
                                    <MenuItem key={day.value} value={day.value}>
                                      {day.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <TextField
                                  label="Start"
                                  type="time"
                                  value={slot.startTime || ''}
                                  onChange={(event) => updateAvailabilitySlot(index, { startTime: event.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ minWidth: 140 }}
                                  disabled={loadingProfile}
                                />
                                <TextField
                                  label="End"
                                  type="time"
                                  value={slot.endTime || ''}
                                  onChange={(event) => updateAvailabilitySlot(index, { endTime: event.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ minWidth: 140 }}
                                  disabled={loadingProfile}
                                />
                                <TextField
                                  select
                                  label="Mode"
                                  value={slot.mode || 'both'}
                                  onChange={(event) => updateAvailabilitySlot(index, { mode: event.target.value })}
                                  sx={{ minWidth: 160 }}
                                  disabled={loadingProfile}
                                >
                                  <MenuItem value="online">Online</MenuItem>
                                  <MenuItem value="in-person">In-person</MenuItem>
                                  <MenuItem value="both">Both</MenuItem>
                                </TextField>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  onClick={() => removeAvailabilitySlot(index)}
                                  disabled={loadingProfile}
                                >
                                  Remove
                                </Button>
                              </Stack>
                            ))}
                          </Stack>
                        )}
                        <Box>
                          <Button variant="outlined" onClick={addAvailabilitySlot} disabled={loadingProfile}>
                            Add availability slot
                          </Button>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        loadingProfile ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />
                      }
                      disabled={loadingProfile}
                    >
                      {loadingProfile ? 'Saving...' : 'Save changes'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          <Card elevation={4} sx={{ borderRadius: CARD_RADIUS }}>
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <LockRoundedIcon />
                </Avatar>
              }
              title={
                <Typography variant="h6" fontWeight={700} sx={{ color: TEXT_COLOR }}>
                  Change password
                </Typography>
              }
              subheader="Strong passwords keep your account secure."
              subheaderTypographyProps={{ sx: { color: TEXT_COLOR } }}
            />
            <Divider />
            <CardContent>
              <Box component="form" onSubmit={updatePassword} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Current password"
                      type="password"
                      name="current"
                      value={passwords.current}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      disabled={loadingPassword}
                      autoComplete="current-password"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="New password"
                      type="password"
                      name="new"
                      value={passwords.new}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      disabled={loadingPassword}
                      autoComplete="new-password"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Confirm new password"
                      type="password"
                      name="confirm"
                      value={passwords.confirm}
                      onChange={handlePasswordChange}
                      fullWidth
                      required
                      disabled={loadingPassword}
                      autoComplete="new-password"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={
 loadingPassword ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />
                        }
                        disabled={loadingPassword}
                      >
 {loadingPassword ? 'Updating...' : 'Update password'}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorSettings;
